import { apiContent } from './chat-content';
import { DEFAULT_IMAGE_MODEL, type ChatMessage, type ImageGenerationOptions, type ModelOption } from '../types/chat';

type ModelsResponse = {
  success: boolean;
  models: ModelOption[];
  default_model: string;
  error?: string;
};

export type StreamDelta = {
  content: string;
  reasoning: string;
};

type StreamOptions = {
  model: string;
  messages: ChatMessage[];
  signal: AbortSignal;
  maxTokens: number;
  onDelta: (delta: StreamDelta) => void;
};

export function parseStreamDelta(raw: string): StreamDelta {
  const empty: StreamDelta = { content: '', reasoning: '' };
  try {
    const data = JSON.parse(raw) as {
      choices?: Array<{
        delta?: {
          content?: string | number;
          reasoning?: string | number;
          reasoning_content?: string | number;
        };
      }>;
      response?: string | number;
    };
    const delta = data.choices?.[0]?.delta;
    if (delta) {
      const content = delta.content != null && String(delta.content) !== '' ? String(delta.content) : '';
      const reasoning = delta.reasoning_content != null
        ? String(delta.reasoning_content)
        : delta.reasoning != null
          ? String(delta.reasoning)
          : '';
      return { content, reasoning };
    }
    if (data.response != null && String(data.response) !== '') {
      return { content: String(data.response), reasoning: '' };
    }
    return empty;
  } catch {
    return empty;
  }
}

export async function fetchModels(): Promise<{ models: ModelOption[]; defaultModel: string }> {
  const response = await fetch('/api/models');
  const data = (await response.json()) as ModelsResponse;
  if (!response.ok || !data.success || !Array.isArray(data.models)) {
    throw new Error(data.error || 'Daftar model tidak tersedia.');
  }
  return { models: data.models, defaultModel: data.default_model };
}

export async function streamChat({ model, messages, signal, maxTokens, onDelta }: StreamOptions): Promise<string> {
  const response = await fetch('/api/chat/stream', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      model,
      messages: messages.slice(-40).map(({ role, content }) => ({ role, content: apiContent(content) })),
      max_tokens: maxTokens,
      temperature: 0.3,
    }),
    signal,
  });

  if (!response.ok || !response.body) {
    const error = (await response.json().catch(() => ({ error: `HTTP ${response.status}` }))) as { error?: string };
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let answer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n');
    const events = buffer.split('\n\n');
    buffer = events.pop() || '';

    for (const event of events) {
      for (const line of event.split('\n')) {
        if (!line.startsWith('data:')) continue;
        const raw = line.slice(5).trim();
        if (!raw || raw === '[DONE]') continue;
        const delta = parseStreamDelta(raw);
        if (!delta.content && !delta.reasoning) continue;
        if (delta.content) answer += delta.content;
        onDelta(delta);
      }
    }
  }

  return answer;
}

type ImageGenerationResponse = {
  data?: Array<{ url?: string; b64_json?: string }>;
  error?: string;
};

export async function generateImage(prompt: string, options: ImageGenerationOptions): Promise<string> {
  const response = await fetch('/api/images/generations', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      model: DEFAULT_IMAGE_MODEL,
      prompt,
      size: options.size,
      quality: options.quality,
      output_format: options.outputFormat,
    }),
  });
  const data = (await response.json().catch(() => ({ error: `HTTP ${response.status}` }))) as ImageGenerationResponse;
  const image = data.data?.[0];
  const url = image?.url || (image?.b64_json ? `data:image/${options.outputFormat};base64,${image.b64_json}` : '');
  if (!response.ok || !url) throw new Error(data.error || 'Provider tidak mengembalikan gambar.');
  return url;
}

export async function checkHealth(): Promise<boolean> {
  try {
    const response = await fetch('/health');
    return response.ok;
  } catch {
    return false;
  }
}
